<template>
  <div>
    <!-- Loading skeleton -->
    <v-container v-if="loading" fluid>
      <v-row justify="center">
        <v-col cols="12" md="10">
          <v-skeleton-loader type="card-heading, divider, list-item-three-line, table" />
        </v-col>
      </v-row>
    </v-container>

    <!-- Main content -->
    <v-container v-else-if="dataEntry && dataEntry.diary_book_id" fluid class="pa-4">

      <!-- ═══ HEADER CARD ═══ -->
      <v-card outlined class="mb-4">
        <v-card-title class="pb-1 d-flex align-center">
          <v-icon left color="primary">mdi-file-document-outline</v-icon>
          <span class="text-h6 font-weight-bold">{{ dataEntry.diary_book_type_name }}</span>
          <v-spacer />
          <v-chip small outlined color="primary" class="mr-2">
            <v-icon left x-small>mdi-pound</v-icon>
            {{ dataEntry.diary_book_code }}
          </v-chip>
          <v-chip small outlined>
            <v-icon left x-small>mdi-calendar</v-icon>
            {{ getDate(dataEntry.effective_date) }}
          </v-chip>
        </v-card-title>

        <v-card-subtitle class="pb-2">
          <v-row dense align="center" class="mt-1">
            <v-col cols="auto">
              <v-chip small color="blue-grey lighten-4" text-color="blue-grey darken-3">
                <v-icon left x-small>mdi-domain</v-icon>
                {{ dataEntry.business_unit_description }}
              </v-chip>
            </v-col>
            <v-col cols="auto" v-if="dataEntry.note">
              <v-chip small color="amber lighten-4" text-color="amber darken-4">
                <v-icon left x-small>mdi-note-text</v-icon>
                {{ dataEntry.note }}
              </v-chip>
            </v-col>
            <v-col cols="auto">
              <v-chip small color="teal lighten-4" text-color="teal darken-3">
                <v-icon left x-small>mdi-currency-usd</v-icon>
                {{ dataEntry.business_unit_currency_code }}
              </v-chip>
            </v-col>
            <v-col cols="auto" v-if="dataEntry.user_fullname">
              <v-chip small outlined>
                <v-icon left x-small>mdi-account</v-icon>
                {{ dataEntry.user_fullname }}
              </v-chip>
            </v-col>
            <v-col cols="auto" v-if="dataEntry.created_date">
              <span class="caption grey--text">
                Creado: {{ getDateTime(dataEntry.created_date) }}
              </span>
            </v-col>
          </v-row>
        </v-card-subtitle>
      </v-card>

      <!-- ═══ KPI SUMMARY ═══ -->
      <v-row dense class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="text-center pa-3">
            <div class="caption grey--text text-uppercase">Débito ({{ transCurrencyCode }})</div>
            <div class="text-h6 font-weight-bold success--text">{{ currencyFormat(totals.transDebit) }}</div>
            <div class="caption grey--text" v-if="hasBaseConversion">
              {{ dataEntry.business_unit_currency_code }} {{ currencyFormat(parseFloat(dataEntry.amount_debit || 0)) }}
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="text-center pa-3">
            <div class="caption grey--text text-uppercase">Crédito ({{ transCurrencyCode }})</div>
            <div class="text-h6 font-weight-bold error--text">{{ currencyFormat(totals.transCredit) }}</div>
            <div class="caption grey--text" v-if="hasBaseConversion">
              {{ dataEntry.business_unit_currency_code }} {{ currencyFormat(parseFloat(dataEntry.amount_credit || 0)) }}
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-card outlined class="text-center pa-3">
            <div class="caption grey--text text-uppercase">Diferencia</div>
            <div class="text-h6 font-weight-bold" :class="Math.abs(totals.transDebit - totals.transCredit) < 0.01 ? 'success--text' : 'warning--text'">
              {{ currencyFormat(Math.abs(totals.transDebit - totals.transCredit)) }}
            </div>
            <v-icon small :color="Math.abs(totals.transDebit - totals.transCredit) < 0.01 ? 'success' : 'warning'">
              {{ Math.abs(totals.transDebit - totals.transCredit) < 0.01 ? 'mdi-check-circle' : 'mdi-alert' }}
            </v-icon>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3" md="2">
          <v-card outlined class="text-center pa-3">
            <div class="caption grey--text text-uppercase">Líneas</div>
            <div class="text-h6 font-weight-bold primary--text">{{ details.length }}</div>
            <div class="caption grey--text">{{ entityCount }} empleados</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3" md="2">
          <v-card outlined class="text-center pa-3">
            <div class="caption grey--text text-uppercase">Cuentas</div>
            <div class="text-h6 font-weight-bold primary--text">{{ accountCount }}</div>
            <div class="caption grey--text">{{ departmentCount }} dptos</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- ═══ TOOLBAR ═══ -->
      <v-card outlined>
        <v-toolbar flat dense>
          <v-btn-toggle v-model="showGrouped" mandatory dense class="mr-4">
            <v-btn :value="true" small>
              <v-icon small left>mdi-group</v-icon> Agrupado
            </v-btn>
            <v-btn :value="false" small>
              <v-icon small left>mdi-format-list-bulleted</v-icon> Detallado
            </v-btn>
          </v-btn-toggle>

          <v-spacer />

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar..."
            single-line hide-details dense outlined
            style="max-width: 300px;"
            class="mr-2"
          />

          <v-chip small outlined class="ml-2">
            {{ displayedItems.length }} registros
          </v-chip>
        </v-toolbar>

        <!-- ═══ DATA TABLE ═══ -->
        <v-data-table
          :headers="tableHeaders"
          :items="displayedItems"
          :search="search"
          :items-per-page="50"
          :dense="displayedItems.length > 30"
          :height="displayedItems.length > 30 ? '55vh' : 'auto'"
          fixed-header
          :footer-props="{
            itemsPerPageText: 'Líneas por página:',
            'items-per-page-options': [30, 50, 100, -1],
            'page-text': '{0}-{1} de {2}'
          }"
          sort-by="chart_of_account_code"
        >
          <!-- Entity -->
          <template v-slot:[`item.entity_name`]="{ item }">
            <span class="text-body-2 font-weight-medium">
              {{ item.entity_name ? item.entity_name.trim() : '—' }}
            </span>
          </template>

          <!-- Account -->
          <template v-slot:[`item.chart_of_account_name`]="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="font-weight-bold text-caption primary--text">{{ item.chart_of_account_code }}</span>
              <span class="text-body-2 grey--text text--darken-3">{{ item.chart_of_account_name }}</span>
            </div>
          </template>

          <!-- Department -->
          <template v-slot:[`item.department_name`]="{ item }">
            <v-chip x-small outlined color="indigo lighten-1" v-if="item.department_name">
              {{ item.department_name }}
            </v-chip>
            <span v-else class="caption grey--text">—</span>
          </template>

          <!-- Location -->
          <template v-slot:[`item.location_description`]="{ item }">
            <v-chip x-small outlined color="grey" v-if="item.location_description">
              {{ item.location_description }}
            </v-chip>
            <span v-else class="caption grey--text">—</span>
          </template>

          <!-- Currency -->
          <template v-slot:[`item.currency_code`]="{ item }">
            <v-chip x-small :color="item.currency_code !== dataEntry.business_unit_currency_code ? 'primary' : 'grey'" outlined>
              {{ item.currency_code }}
            </v-chip>
          </template>

          <!-- Note -->
          <template v-slot:[`item.note`]="{ item }">
            <span class="caption">{{ item.note }}</span>
          </template>

          <!-- Category -->
          <template v-slot:[`item.category_name`]="{ item }">
            <span class="caption">{{ item.category_name || '—' }}</span>
          </template>

          <!-- Subcategory -->
          <template v-slot:[`item.subcategory_name`]="{ item }">
            <span class="caption">{{ item.subcategory_name || '—' }}</span>
          </template>

          <!-- Debit -->
          <template v-slot:[`item.debit`]="{ item }">
            <span class="font-weight-bold success--text" v-if="item.entry_type_id === 1">
              {{ currencyFormat(item.amount) }}
            </span>
            <span v-else class="caption grey--text">—</span>
          </template>

          <!-- Credit -->
          <template v-slot:[`item.credit`]="{ item }">
            <span class="font-weight-bold error--text" v-if="item.entry_type_id === 2">
              {{ currencyFormat(item.amount) }}
            </span>
            <span v-else class="caption grey--text">—</span>
          </template>
        </v-data-table>
      </v-card>

    </v-container>
  </div>
</template>
<script>
import moment from "moment";
export default {
  data: () => ({
    dataEntry: {},
    search: "",
    loading: false,
    showGrouped: true,
  }),
  created() {
    this.getData();
  },
  computed: {
    details() {
      return this.dataEntry.diary_book_details || [];
    },
    transCurrencyCode() {
      if (!this.details.length) return '';
      return this.details[0].currency_code || 'DOP';
    },
    hasBaseConversion() {
      if (!this.details.length) return false;
      return this.transCurrencyCode !== this.dataEntry.business_unit_currency_code;
    },
    totals() {
      let transDebit = 0;
      let transCredit = 0;
      this.details.forEach(d => {
        if (d.entry_type_id === 1) transDebit += parseFloat(d.amount || 0);
        if (d.entry_type_id === 2) transCredit += parseFloat(d.amount || 0);
      });
      return { transDebit, transCredit };
    },
    entityCount() {
      return (this.dataEntry.entities || []).length;
    },
    accountCount() {
      return (this.dataEntry.chart_of_accounts || []).length;
    },
    departmentCount() {
      return (this.dataEntry.departments || []).filter(d => d.department_id != null).length;
    },
    displayedItems() {
      const rows = this.details.map(d => ({
        ...d,
        entity_name: d.entity_name ? d.entity_name.trim() : (d.note || ''),
        department_name: this.getDepartmentName(d),
      }));

      if (!this.showGrouped) return rows;

      const groups = {};
      rows.forEach(line => {
        const key = `${line.chart_of_account_id}|${line.department_id}|${line.location_id}|${line.entry_type_id}|${line.category_id}|${line.subcategory_id}`;
        if (!groups[key]) {
          groups[key] = { ...line, amount: 0, _count: 0 };
        }
        groups[key].amount += parseFloat(line.amount || 0);
        groups[key]._count++;
      });
      return Object.values(groups);
    },
    tableHeaders() {
      const base = [
        { text: "Cuenta Contable", value: "chart_of_account_name", width: "22%" },
        { text: "Departamento", value: "department_name" },
        { text: "Sucursal", value: "location_description" },
        { text: "Categoría", value: "category_name" },
        { text: "Subcategoría", value: "subcategory_name" },
        { text: "Concepto", value: "note", width: "8%" },
        { text: "Moneda", value: "currency_code", align: "center", width: "6%" },
        { text: "Débito", value: "debit", align: "end" },
        { text: "Crédito", value: "credit", align: "end" },
      ];

      if (!this.showGrouped) {
        base.splice(1, 0, { text: "Empleado", value: "entity_name", width: "18%" });
      }

      return base;
    },
  },
  methods: {
    getDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    getDateTime(date) {
      return moment(date).format("DD/MM/YYYY hh:mm A");
    },
    getDepartmentName(detail) {
      if (!detail.departments || !detail.departments.length) return null;
      const dept = detail.departments[0];
      return dept ? dept.name : null;
    },
    async getData() {
      this.loading = true;
      let id = this.$route.params.id;
      let filters = " and diary_book_id=" + id + " ";
      const responseRequest = await this.api_Post("accounting/diary_book", {
        filtros: filters,
      });
      let data = await responseRequest.json();
      this.dataEntry = data[0];
      this.loading = false;
    },
  },
};
</script>
